import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI } from '../utils/api';
import { initSocket, disconnectSocket, getSocket } from '../utils/socket';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser]       = useState(null);
  const [token, setToken]     = useState(() => localStorage.getItem('nexus_token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) { setLoading(false); return; }
    authAPI.me()
      .then(({ data }) => {
        setUser(data);
        const socket = initSocket(token);
        socket.emit('user:online', data._id || data.id);
      })
      .catch(() => {
        localStorage.removeItem('nexus_token');
        setToken(null);
      })
      .finally(() => setLoading(false));
  }, [token]);

  // login can accept either email+password OR a pre-existing token+user (from OTP flow)
  const login = useCallback(async (email, password, existingToken, existingUser) => {
    if (existingToken && existingUser) {
      localStorage.setItem('nexus_token', existingToken);
      setToken(existingToken);
      setUser(existingUser);
      const socket = initSocket(existingToken);
      socket.emit('user:online', existingUser._id || existingUser.id);
      return { token: existingToken, user: existingUser };
    }
    const { data } = await authAPI.login(email, password);
    localStorage.setItem('nexus_token', data.token);
    setToken(data.token);
    setUser(data.user);
    const socket = initSocket(data.token);
    socket.emit('user:online', data.user._id || data.user.id);
    return data;
  }, []);

  const register = useCallback(async (formData) => {
    const { data } = await authAPI.register(formData);
    localStorage.setItem('nexus_token', data.token);
    setToken(data.token);
    setUser(data.user);
    const socket = initSocket(data.token);
    socket.emit('user:online', data.user._id || data.user.id);
    return data;
  }, []);

  const logout = useCallback(async () => {
    try { await authAPI.logout(); } catch {}
    const socket = getSocket();
    if (socket) socket.disconnect();
    disconnectSocket();
    localStorage.removeItem('nexus_token');
    setToken(null);
    setUser(null);
  }, []);

  const updateUser = useCallback((updates) => {
    setUser(prev => ({ ...prev, ...updates }));
  }, []);

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
